import { createFeatureSelector, createSelector } from '@ngrx/store';

import { ShippingTypes } from '../models/shipping.models';

import { ShippingState } from './shipping.state';

import { PromotionOverData } from '@features/promotion';

/**
 * Selector para obtener el State de Shipping
 *
 */
const selectShippingState = createFeatureSelector<ShippingState>('shippingShared');

export const getSelectedShippingAddress = createSelector(
    selectShippingState,
    (shippingState: ShippingState) => shippingState?.selectedAddress
);

export const getPromotionsWithAmountOver = (
    promotions: PromotionOverData['delivery_promotions']
) => promotions?.filter((promo) => promo.amount_over >= 0);

export const getStandardPromotionsWithAmountOver = (
    promotions: PromotionOverData['delivery_promotions']
) => promotions?.filter(
    (promo) => promo.amount_over >= 0 && promo.delivery_method === ShippingTypes.STANDARD
);

export const getFirstFieldValue = (
    promotions: PromotionOverData['delivery_promotions'],
    field: keyof typeof promotions[0]
): number => promotions?.map((promo) => promo[field])[0] as number;

/**
 * Selector para obtener los métodos de envío
 */
export const getShippingMethods = createSelector(
    selectShippingState,
    (shippingState: ShippingState) => shippingState?.values
);

/**
 * Selector para obtener el método de envío seleccionado
 */
export const getSelectedShippingMethod = createSelector(
    selectShippingState,
    (shippingState: ShippingState) => shippingState?.selectedShippingMethod
);

export const getShippingStateLoaded = createSelector(
    selectShippingState,
    (shippingState: ShippingState) => shippingState?.loaded
);

export const getFreeShippingOverPromoData = createSelector(
    selectShippingState,
    (shippingState: ShippingState) => shippingState?.promotions.freeShippingOverPromoData
);

/**
* Selector para obtener si hay promocion de freeShipping sin precio mínimo
*/
export const getFreePromotion = createSelector(
    getFreeShippingOverPromoData,
    (shippingOverPromoData: PromotionOverData) => {
        return shippingOverPromoData?.delivery_promotions?.filter((promo) => promo.amount_over === 0);
    }
);

/**
 * Selector para obtener si hay promocion/es de freeShippingOver
 */
export const getFreeShippingOverPromotion = createSelector(
    getFreeShippingOverPromoData,
    (shippingOverPromoData: PromotionOverData) => {
        return shippingOverPromoData?.delivery_promotions?.filter((promo) => promo.amount_over > 0);
    }
);

/**
 * Selector para obtener si esta activa la promoción de shippingOver
 */
export const getFreeShippingOverPromotionActive = createSelector(
    getFreeShippingOverPromoData,
    (shippingOverPromoData: PromotionOverData) => shippingOverPromoData?.calculation_status?.global_status
);

/**
 * Selector para obtener el estado de las promociones
 */
export const getPromotionsStatus = createSelector(
    selectShippingState,
    (shippingState: ShippingState) => shippingState?.promotions?.promosStatus.status
);

export const getFreeShippingOverAmountOver = createSelector(
    getFreeShippingOverPromoData,
    (shippingOverPromoData: PromotionOverData) =>
        getFirstFieldValue(
            getPromotionsWithAmountOver(shippingOverPromoData?.delivery_promotions),
            'amount_over'
        )
);

export const getFreeShippingDeliveryPromotion = createSelector(
    getFreeShippingOverPromoData,
    (shippingOverPromoData: PromotionOverData) =>
        getFirstFieldValue(
            getStandardPromotionsWithAmountOver(shippingOverPromoData?.delivery_promotions),
            'amount_over'
        )
);

/**
 * Selector para saber si se deber mostrar el texto de FSOFullPrice del freeShippingOver
 */
export const getFreeShippingOverFSOFullPrice = createSelector(
    getFreeShippingOverPromoData,
    (shippingOverPromoData: PromotionOverData) => {
        return shippingOverPromoData?.delivery_promotions?.filter(
            (promos) => promos.amount_over > 0
        ).map((promo) => promo?.exclude_sales_items)[0];
    }
);

/**
 * Selector para la cantidad pendiente hasta llegar al freeShipping
 */
export const getFreeShippingOverAmountPending = createSelector(
    getFreeShippingOverPromoData,
    (shippingOverPromoData: PromotionOverData) => {
        return shippingOverPromoData?.delivery_promotions?.filter(
            (promos) => promos.amount_pending > 0
        ).map((promo) => promo.amount_pending)[0];
    }
);

export const getHasFreeShipping = createSelector(
    getFreeShippingOverPromoData,
    (shippingOverPromoData: PromotionOverData) => {
        return !!shippingOverPromoData?.delivery_promotions?.find(
            (promo) => promo.delivery_method === ShippingTypes.STANDARD
        );
    }
);

export const getFreeShippingExcludeSalesItems = createSelector(
    getFreeShippingOverPromoData,
    (shippingOverPromoData: PromotionOverData) => {
        return shippingOverPromoData?.delivery_promotions?.filter(
            (promo) => promo.delivery_method === ShippingTypes.STANDARD
        )?.map((promo) => promo?.exclude_sales_items)?.[0];
    }
);
